import NextImage from 'next/image';
import { useTranslation } from 'next-i18next';
import { FC } from 'react';

import { HOME_SERVICES_IMAGES } from '../../../../config';

import { QrCodeBanner } from '@/components/QrCodeBanner/QrCodeBanner';
import { COMMON_LOCALE_PART } from '@/locale';

const TOP_BORDER_CARDS_INDEXES: [number, number, number] = [3, 4, 5];
const X_BORDER_CARDS_INDEXES: [number, number] = [1, 4];

enum AdditionalServicesKey {
  PlantsAroundTheWorld = 1,
  MoreAboutPlants,
  JoinTours,
  BuyPlants,
  Workshops,
  FindInfo,
}

interface IAdditionalServiceData {
  serviceKey: AdditionalServicesKey;
  Icon: string;
}

const additionalServicesData: IAdditionalServiceData[] = [
  {
    serviceKey: AdditionalServicesKey.PlantsAroundTheWorld,
    Icon: HOME_SERVICES_IMAGES.ForrestIcon,
  },
  {
    serviceKey: AdditionalServicesKey.MoreAboutPlants,
    Icon: HOME_SERVICES_IMAGES.MagGlassIcon,
  },
  {
    serviceKey: AdditionalServicesKey.JoinTours,
    Icon: HOME_SERVICES_IMAGES.MapIcon,
  },
  {
    serviceKey: AdditionalServicesKey.BuyPlants,
    Icon: HOME_SERVICES_IMAGES.ExchangeIcon,
  },
  {
    serviceKey: AdditionalServicesKey.Workshops,
    Icon: HOME_SERVICES_IMAGES.BookIcon,
  },
  {
    serviceKey: AdditionalServicesKey.FindInfo,
    Icon: HOME_SERVICES_IMAGES.HandsIcon,
  },
];

interface AdditionalServicesItemProps {
  withXBorders: boolean;
  withTopBorder: boolean;
  text: string;
  Icon: string;
}

const AdditionalServicesItem = ({ withTopBorder, withXBorders, Icon, text }: AdditionalServicesItemProps) => (
  <div className={`h-40 py-2 lg:py-3 ${withTopBorder ? 'border-t' : ''} md:border-t-0`}>
    <div
      className={`flex h-full flex-col items-center justify-between px-2 ${
        withXBorders ? 'border-x' : ''
      } md:border-x-0`}
    >
      <NextImage src={Icon} width={74} height={74} loading="lazy" alt={text} />
      <p className="h-1/2 text-center text-sm font-bold md:text-base-md lg:h-2/6">{text}</p>
    </div>
  </div>
);

export const AdditionalServices: FC = () => {
  const { t } = useTranslation(COMMON_LOCALE_PART);

  return (
    <section className="relative mt-16">
      <div className="container relative z-10 mb-[400px] md:mb-[420px] lg:mb-[435px]">
        <h3 className="mb-4 text-lg font-bold text-black-900 md:text-[28px] lg:text-[32px]">
          {t('additional-services.title')}
        </h3>
        <div className="grid grid-cols-3 rounded-lg bg-white py-3 md:grid-cols-6 md:divide-x md:py-6">
          {additionalServicesData.map(({ serviceKey, Icon }, index) => (
            <AdditionalServicesItem
              key={serviceKey}
              Icon={Icon}
              text={t(`additional-services.card${serviceKey}`)}
              withXBorders={X_BORDER_CARDS_INDEXES.includes(index)}
              withTopBorder={TOP_BORDER_CARDS_INDEXES.includes(index)}
            />
          ))}
        </div>
      </div>
      <QrCodeBanner />
    </section>
  );
};
